import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  AgricultureOutlined,
  CalendarMonthOutlined,
  ChecklistOutlined,
  Close as CloseIcon,
  ContentCut,
  Event as EventIcon,
  GroupOutlined,
  HourglassEmptyOutlined,
  LocalShippingOutlined,
  PersonOutline,
  WarningAmberOutlined,
  WorkOutline,
} from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { animalSpeciesHelper, formatPhoneNumber } from '@farmshare/utils';

import { vendorState } from 'state';
import {
  EnumProcessorSchedulingStatus,
  ProcessorScheduling,
  SortFindManyProcessorSchedulingInput,
  SortFindManyUserInput,
  useProcessorCustomersPaginationLazyQuery,
  useProcessorSchedulingFilteredLazyQuery,
  User,
  useVendorHerdAnimalsLazyQuery,
} from '../../lib/graphql';
import { renderStatusText } from 'pages/processor/lib/helpers/processorStatusPresentation';

interface GlobalSearchModalProps {
  onClose: () => void;
  allStatuses: EnumProcessorSchedulingStatus[];
}

interface QuickAction {
  label: string;
  description: string;
  icon: ReactNode;
  to: (() => string) | string;
}

interface QuickActionGroup {
  title: string;
  actions: QuickAction[];
}

const JOB_ISSUE_VALUES = [
  'missingCutsheet',
  'missingInspectionLevel',
  'missingSplitType',
  'missingBuyer',
];

const jobsLink =
  (opts: {
    dateField?: 'dropoff' | 'harvest' | 'cut';
    range?: 'today' | 'thisWeek';
    withIssues?: boolean;
  }) =>
  () => {
    const params = new URLSearchParams();
    if (opts.dateField && opts.range) {
      const from =
        opts.range === 'today'
          ? moment().format('YYYY-MM-DD')
          : moment().startOf('week').format('YYYY-MM-DD');
      const to =
        opts.range === 'today'
          ? moment().format('YYYY-MM-DD')
          : moment().endOf('week').format('YYYY-MM-DD');
      params.set('f.date', opts.dateField);
      params.set('f.date.from', from);
      params.set('f.date.to', to);
    }
    if (opts.withIssues) {
      params.set('f.issues', JOB_ISSUE_VALUES.join(','));
    }
    return `/processor/jobs?${params.toString()}`;
  };

/** Fixed placeholder keys for the search-results loading skeleton rows. */
const SEARCH_SKELETON_KEYS = [
  'skeleton-1',
  'skeleton-2',
  'skeleton-3',
  'skeleton-4',
  'skeleton-5',
];

const QUICK_ACTION_COLUMNS: QuickActionGroup[][] = [
  [
    {
      title: 'Quick Links',
      actions: [
        {
          label: 'Dashboard',
          description: 'Key metrics and today’s actions',
          icon: <ChecklistOutlined fontSize="small" />,
          to: '/processor/dashboard',
        },
        {
          label: 'Calendar',
          description: 'Browse jobs by date',
          icon: <CalendarMonthOutlined fontSize="small" />,
          to: '/processor/calendar',
        },
        {
          label: 'All Jobs',
          description: 'Search and filter every job',
          icon: <WorkOutline fontSize="small" />,
          to: '/processor/jobs',
        },
        {
          label: 'All Customers',
          description: 'Browse and manage customers',
          icon: <GroupOutlined fontSize="small" />,
          to: '/processor/customers',
        },
        {
          label: 'All Animals',
          description: 'Browse and manage animals',
          icon: <AgricultureOutlined fontSize="small" />,
          to: '/processor/animals',
        },
        {
          label: 'Cutsheet Builder',
          description: 'Create and edit cutsheets',
          icon: <ContentCut fontSize="small" />,
          to: '/processor/cutsheets-v2',
        },
        {
          label: 'Waitlist',
          description: 'Customers waiting for a slot',
          icon: <HourglassEmptyOutlined fontSize="small" />,
          to: '/processor/settings/waitlist',
        },
      ],
    },
  ],
  [
    {
      title: 'Today',
      actions: [
        {
          label: 'Drop-offs today',
          description: 'Jobs scheduled to drop off today',
          icon: <LocalShippingOutlined fontSize="small" />,
          to: jobsLink({ dateField: 'dropoff', range: 'today' }),
        },
        {
          label: 'Cuts today',
          description: 'Jobs scheduled to cut today',
          icon: <ContentCut fontSize="small" />,
          to: jobsLink({ dateField: 'cut', range: 'today' }),
        },
        {
          label: 'Needs attention',
          description:
            "Today's drop-offs missing cutsheet, inspection, split, or buyer",
          icon: <WarningAmberOutlined fontSize="small" />,
          to: jobsLink({
            dateField: 'dropoff',
            range: 'today',
            withIssues: true,
          }),
        },
      ],
    },
    {
      title: 'This Week',
      actions: [
        {
          label: 'Drop-offs this week',
          description: 'Jobs scheduled to drop off this week',
          icon: <LocalShippingOutlined fontSize="small" />,
          to: jobsLink({ dateField: 'dropoff', range: 'thisWeek' }),
        },
        {
          label: 'Cuts this week',
          description: 'Jobs scheduled to cut this week',
          icon: <ContentCut fontSize="small" />,
          to: jobsLink({ dateField: 'cut', range: 'thisWeek' }),
        },
        {
          label: 'Needs attention',
          description:
            "This week's drop-offs missing cutsheet, inspection, split, or buyer",
          icon: <WarningAmberOutlined fontSize="small" />,
          to: jobsLink({
            dateField: 'dropoff',
            range: 'thisWeek',
            withIssues: true,
          }),
        },
      ],
    },
  ],
];

export function GlobalSearchModal({
  onClose,
  allStatuses,
}: GlobalSearchModalProps) {
  const vendor = useRecoilValue(vendorState);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<
    'all' | 'customers' | 'jobs' | 'animals'
  >('all');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, []);

  const [loadJobs, { data: jobsData, loading: jobsLoading }] =
    useProcessorSchedulingFilteredLazyQuery({
      fetchPolicy: 'network-only',
    });

  const [loadCustomers, { data: customersData, loading: customersLoading }] =
    useProcessorCustomersPaginationLazyQuery({
      fetchPolicy: 'network-only',
    });

  // Floating (unassigned) herd animals — the intake pool. Assigned animals stay
  // reachable through the Jobs results, so they're left out here.
  const [loadAnimals, { data: animalsData, loading: animalsLoading }] =
    useVendorHerdAnimalsLazyQuery({
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm && vendor?._id) {
      loadJobs({
        variables: {
          searchByContact: debouncedSearchTerm,
          vendorId: vendor._id,
          statusFilter: allStatuses,
          sort: SortFindManyProcessorSchedulingInput.DropoffdateDesc,
          page: 1,
          perPage: 20,
        },
      });

      loadCustomers({
        variables: {
          search: debouncedSearchTerm,
          vendorId: vendor._id,
          sort: SortFindManyUserInput.NameAsc,
          page: 1,
          perPage: 20,
        },
      });

      loadAnimals({
        variables: {
          scope: 'unassigned',
          search: debouncedSearchTerm,
          page: 1,
          perPage: 20,
        },
      });
    }
  }, [
    debouncedSearchTerm,
    vendor?._id,
    loadJobs,
    loadCustomers,
    loadAnimals,
    allStatuses,
  ]);

  const handleViewOnCalendar = (job: ProcessorScheduling) => {
    const dateToNavigate = moment.utc(job.dropoffDate).format('YYYY-MM-DD');
    navigate(`/processor/calendar?selectedDate=${dateToNavigate}`);
    onClose();
  };

  const getJobSummary = (job: ProcessorScheduling) => {
    const animals = job.animalHeads?.length || 0;
    const species = job.animalSpecies || 'Unknown';
    return `${animals} ${species}`;
  };

  const jobs = (jobsData?.getFilteredProcessorScheduling?.items ||
    []) as ProcessorScheduling[];
  const customers = (customersData?.getProcessorCustomers?.items ||
    []) as User[];
  const animals = (animalsData?.vendorHerdAnimals?.items ?? []).filter(
    (a): a is NonNullable<typeof a> => Boolean(a),
  );

  const isLoading = jobsLoading || customersLoading || animalsLoading;
  const showCustomers = activeTab === 'all' || activeTab === 'customers';
  const showJobs = activeTab === 'all' || activeTab === 'jobs';
  const showAnimals = activeTab === 'all' || activeTab === 'animals';
  const isEmpty =
    !isLoading &&
    ((activeTab === 'all' &&
      jobs.length === 0 &&
      customers.length === 0 &&
      animals.length === 0) ||
      (activeTab === 'customers' && customers.length === 0) ||
      (activeTab === 'jobs' && jobs.length === 0) ||
      (activeTab === 'animals' && animals.length === 0));

  return (
    <Box
      sx={{
        // Fill the sheet on a phone, where the modal is fullscreen — a fixed
        // 560px left the results stopping partway down with dead space under
        // them. `100%` resolves because MUI's Dialog paper is a flex column and
        // DialogContent is its flex child, so the height above is definite.
        // Desktop keeps the fixed height: there the modal is a centred card and
        // 560 is what stops it growing with the result count.
        height: { xs: '100%', md: 560 },
        display: 'flex',
        flexDirection: 'column',
        mx: -1,
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
          pb: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            placeholder="Search by name, company, email, phone, or animal tag..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputRef={inputRef}
          />
          <IconButton onClick={onClose} aria-label="Close search">
            <CloseIcon />
          </IconButton>
        </Stack>
        {debouncedSearchTerm && (
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}
          >
            <Tab
              label={`All${
                !isLoading
                  ? ` (${customers.length + jobs.length + animals.length})`
                  : ''
              }`}
              value="all"
            />
            <Tab
              label={`Customers${!isLoading ? ` (${customers.length})` : ''}`}
              value="customers"
            />
            <Tab
              label={`Jobs${!isLoading ? ` (${jobs.length})` : ''}`}
              value="jobs"
            />
            <Tab
              label={`Animals${!isLoading ? ` (${animals.length})` : ''}`}
              value="animals"
            />
          </Tabs>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {!debouncedSearchTerm && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
              },
              gap: 3,
              alignItems: 'flex-start',
            }}
          >
            {QUICK_ACTION_COLUMNS.map((column) => (
              <Stack key={column[0]?.title ?? 'column'} spacing={1}>
                {column.map((group) => (
                  <Box key={group.title}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ letterSpacing: 1, display: 'block', mb: 1 }}
                    >
                      {group.title}
                    </Typography>
                    <Stack spacing={1}>
                      {group.actions.map((action) => (
                        <ButtonBase
                          key={action.label}
                          onClick={() => {
                            const href =
                              typeof action.to === 'function'
                                ? action.to()
                                : action.to;
                            navigate(href);
                            onClose();
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 1.5,
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            color: 'text.primary',
                            textAlign: 'left',
                            transition:
                              'border-color 0.15s ease, background-color 0.15s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: 'primary.main',
                            }}
                          >
                            {action.icon}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {action.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', lineHeight: 1.3 }}
                            >
                              {action.description}
                            </Typography>
                          </Box>
                        </ButtonBase>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ))}
          </Box>
        )}

        {debouncedSearchTerm && isLoading && (
          <List>
            {SEARCH_SKELETON_KEYS.map((skeletonKey) => (
              <ListItem key={skeletonKey} disablePadding sx={{ px: 2, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Skeleton variant="circular" width={24} height={24} />
                </ListItemIcon>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="50%" height={24} />
                  <Skeleton variant="text" width="75%" height={20} />
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        {debouncedSearchTerm && !isLoading && (
          <>
            {showCustomers && customers.length > 0 && (
              <List sx={{ mb: activeTab === 'all' ? 2 : 0 }}>
                {customers.map((customer: User) => {
                  const name =
                    customer.company ||
                    `${customer.first_name} ${customer.last_name}`;
                  const contactParts = [
                    customer.email,
                    customer.phone && formatPhoneNumber(customer.phone),
                  ].filter(Boolean);
                  return (
                    <ListItem key={customer._id} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(`/processor/customers/${customer._id}`);
                          onClose();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <PersonOutline color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={name}
                          primaryTypographyProps={{ noWrap: true }}
                          secondary={contactParts.join(' · ') || undefined}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {showJobs && jobs.length > 0 && (
              <List>
                {jobs.map((job: ProcessorScheduling) => {
                  const name =
                    job.requestedBy?.company ||
                    `${job.requestedBy?.first_name} ${job.requestedBy?.last_name}`;
                  const summary = getJobSummary(job);
                  const dropoff = moment
                    .utc(job.dropoffDate)
                    .format('MMM DD, YYYY');
                  const status = renderStatusText(job.status);
                  return (
                    <ListItem
                      key={job._id}
                      disablePadding
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleViewOnCalendar(job)}
                          size="small"
                          aria-label="View on calendar"
                        >
                          <EventIcon />
                        </IconButton>
                      }
                    >
                      <ListItemButton
                        onClick={() => {
                          navigate(`/processing-job/${job._id}`);
                          onClose();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <WorkOutline color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${name} · ${summary}`}
                          primaryTypographyProps={{ noWrap: true }}
                          secondary={`Drop-off ${dropoff} · ${status}`}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {showAnimals && animals.length > 0 && (
              <List>
                {animals.map((animal) => {
                  const speciesLabel = animal.species
                    ? animalSpeciesHelper(animal.species).label
                    : 'Animal';
                  const producer =
                    animal.producerRef?.company ||
                    [
                      animal.producerRef?.first_name,
                      animal.producerRef?.last_name,
                    ]
                      .filter(Boolean)
                      .join(' ')
                      .trim() ||
                    animal.producerIdentifier ||
                    'Unknown producer';
                  // Land on the herd page pre-filtered to this animal (by tag,
                  // else producer) so it's the first thing they see.
                  const seed = animal.tracking || producer;
                  return (
                    <ListItem key={animal._id} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(
                            `/processor/animals?search=${encodeURIComponent(
                              seed,
                            )}`,
                          );
                          onClose();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <AgricultureOutlined color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            animal.tracking
                              ? `${speciesLabel} · ${animal.tracking}`
                              : speciesLabel
                          }
                          primaryTypographyProps={{ noWrap: true }}
                          secondary={`${producer} · ${
                            animal.status || 'Unassigned'
                          }`}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {isEmpty && (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No {activeTab === 'all' ? 'results' : activeTab} found matching
                &quot;{debouncedSearchTerm}&quot;
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
